import React from "react";
import { Form, Button } from "react-bootstrap";
import TextInput from "../components/TextInput";
import CommonHeader from "../containers/CommonHeader";
import { BirthdayInput, PhoneInput } from "../components/NumberInput";
import {
  validatePhoneNumber,
  validatePassword,
  PASSWORD_MIN_LENGTH,
} from "../utils/validate";
import {
  apiError,
  errorMessage,
  registerUser,
  sendResetCode,
} from "../services/fidelity";
import { useDispatch, useSelector } from "react-redux";
import { setUserPhone, setUserId } from "../ducks/userSlice";
import useToast from "../hooks/useToast";
import useGoTo from "../hooks/useGoTo";
import useFinishLogin from "../hooks/useFinishLogin";

export default function RegisterScreen() {
  const dispatch = useDispatch();
  const goTo = useGoTo();
  const toast = useToast();
  const finishLogin = useFinishLogin();
  const { userPhone } = useSelector((state) => state.user);
  const [name, setName] = React.useState("");
  const [birthday, setBirthday] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [passwordConfirmation, setPasswordConfirmation] = React.useState("");
  const [isLoading, setLoading] = React.useState(false);
  const [isPolicyChecked, setPolicyChecked] = React.useState(false);

  const onChangePhone = (event) => {
    dispatch(setUserPhone({ phone: event.target.value }));
  };

  const onChangeName = (event) => {
    setName(event.target.value);
  };

  const onChangeBirthday = (event) => {
    setBirthday(event.target.value);
  };

  const onChangePassword = (event) => {
    setPassword(event.target.value);
  };

  const onChangePasswordConfirmation = (event) => {
    setPasswordConfirmation(event.target.value);
  };

  const onChangePolicy = (event) => {
    setPolicyChecked(event.target.checked);
  };

  // Telefone já cadastrado sem senha (base antiga): em vez de barrar, manda
  // criar a senha com o código.
  const askCode = async () => {
    const response = await sendResetCode(userPhone).then((resp) => resp.json());
    if (response.error) {
      throw apiError(response, "Não foi possível enviar o código.");
    }

    dispatch(setUserId({ id: response.id, sendCodeChannel: response.channel }));
    goTo("/confirmation");
  };

  const register = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const formatBirthday = birthday.split("-").reverse().join("-");
      const response = await registerUser(
        name,
        userPhone,
        formatBirthday,
        password,
      ).then((resp) => resp.json());

      if (response.error) {
        const error = apiError(response, "Erro ao realizar o cadastro!");
        if (error.code === "TELEFONE_SEM_SENHA") {
          await askCode();
          return;
        }

        if (error.code === "TELEFONE_CADASTRADO") {
          toast.toastError("Telefone já cadastrado. Entre com sua senha.");
          goTo("/login");
          return;
        }

        throw error;
      }

      finishLogin(response);
    } catch (error) {
      toast.toastError(errorMessage(error, "Erro ao realizar o cadastro!"));
    } finally {
      setLoading(false);
    }
  };

  const nameError =
    name.length > 0 && name.length < 4 ? "Nome com no mínimo 4 caracteres" : "";

  const phoneError = validatePhoneNumber(userPhone) ? "" : "Celular inválido";

  const birthdayError =
    birthday && birthday.trim().length < 10 ? "Data inválida" : "";

  const passwordError =
    password.length > 0 && !validatePassword(password)
      ? `Senha com no mínimo ${PASSWORD_MIN_LENGTH} caracteres`
      : "";

  const passwordConfirmationError =
    passwordConfirmation.length > 0 && passwordConfirmation !== password
      ? "As senhas não conferem"
      : "";

  const isRegisterDisabled =
    !!nameError ||
    !!phoneError ||
    !!birthdayError ||
    !!passwordError ||
    !!passwordConfirmationError ||
    !validatePassword(password) ||
    passwordConfirmation !== password;

  return (
    <>
      <CommonHeader text="Complete seu cadastro" />
      <div className="mt-3 text-center">
        Só precisamos de mais algumas informações!
      </div>
      <Form className="mt-4 px-3 px-lg-0" onSubmit={register}>
        <TextInput
          className="mt-3"
          label="Celular com DDD"
          value={userPhone}
          onChange={onChangePhone}
          error={!!phoneError}
          helperText={phoneError}
          inputComponent={PhoneInput}
          inputProps={{ autoComplete: "username" }}
          required
          disabled={isLoading}
        />
        <TextInput
          className="mt-3"
          autoFocus
          value={name}
          onChange={onChangeName}
          error={!!nameError}
          helperText={nameError}
          type="text"
          label="Nome"
          required
          disabled={isLoading}
        />
        <TextInput
          className="mt-3"
          value={birthday}
          onChange={onChangeBirthday}
          error={!!birthdayError}
          helperText={birthdayError}
          label="Data de nascimento"
          inputComponent={BirthdayInput}
          required
          disabled={isLoading}
        />
        <TextInput
          className="mt-3"
          type="password"
          label="Senha"
          value={password}
          onChange={onChangePassword}
          error={!!passwordError}
          helperText={passwordError}
          inputProps={{ autoComplete: "new-password" }}
          required
          disabled={isLoading}
        />
        <TextInput
          className="mt-3"
          type="password"
          label="Confirme a senha"
          value={passwordConfirmation}
          onChange={onChangePasswordConfirmation}
          error={!!passwordConfirmationError}
          helperText={passwordConfirmationError}
          inputProps={{ autoComplete: "new-password" }}
          required
          disabled={isLoading}
        />
        <Form.Check
          id="register-policy"
          className="mt-4"
          type="checkbox"
          checked={isPolicyChecked}
          onChange={onChangePolicy}
          label={
            <>
              {`Li e concordo com a `}
              <a
                href="https://sistemapallas.com.br/politica-de-privacidade.html"
                target="_blank"
                rel="noreferrer"
              >
                Política de Privacidade.
              </a>
            </>
          }
        />
        <div className="form-footer-spacer" aria-hidden="true" />
        <div className="form-footer fixed-bottom">
          <Button
            className="w-100 mt-3"
            variant="shop"
            type="submit"
            disabled={isRegisterDisabled || isLoading || !isPolicyChecked}
          >
            {isLoading ? "Enviando..." : "Cadastre-se"}
          </Button>
        </div>
      </Form>
    </>
  );
}
